import { useSearchParams, Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { LayoutDashboard, Package, FolderTree, ShoppingCart, Tag, Settings, LogOut, ExternalLink } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useSeo } from "../../lib/hooks";
import AdminOverview from "./AdminOverview";
import AdminProducts from "./AdminProducts";
import AdminCategories from "./AdminCategories";
import AdminOrders from "./AdminOrders";
import AdminPromos from "./AdminPromos";
import AdminSettings from "./AdminSettings";
import AdminPushSetup from "./AdminPushSetup";
import AdminMarkets from "./AdminMarkets";
import AdminPages from "./AdminPages";
import { CalendarDays, FileText } from "lucide-react";

const TABS = [
  ["apercu", "Tableau de bord", LayoutDashboard, AdminOverview],
  ["produits", "Produits & stocks", Package, AdminProducts],
  ["categories", "Catégories", FolderTree, AdminCategories],
  ["commandes", "Commandes", ShoppingCart, AdminOrders],
  ["promos", "Codes promo", Tag, AdminPromos],
  ["marches", "Marchés", CalendarDays, AdminMarkets],
  ["pages", "Pages légales", FileText, AdminPages],
  ["reglages", "Livraison & réglages", Settings, AdminSettings],
];

function OrderAlerts() {
  const [permission, setPermission] = useState(typeof Notification === "undefined" ? "unsupported" : Notification.permission);
  const [enabled, setEnabled] = useState(false);
  const known = useRef(null);
  const enabledRef = useRef(false);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);
  useEffect(() => {
    let active = true;
    const check = async () => {
      try {
        const { data } = await api.get("/admin/orders");
        if (!active || !Array.isArray(data)) return;
        const ids = new Set(data.map(o => o.id).filter(Boolean));
        if (known.current && enabledRef.current && typeof Notification !== "undefined" && Notification.permission === "granted") {
          const newcomers = data.filter(o => o.id && !known.current.has(o.id) && o.payment_status === "paid");
          if (newcomers.length) {
            const notice = new Notification("NasDeal · Nouvelle commande", {
              body: newcomers.length === 1 ? "Une commande payée vient d'arriver. Ouvrez l'administration." : `${newcomers.length} nouvelles commandes payées. Ouvrez l'administration.`,
              icon: "/logo-nasdeal.jpg",
              tag: "nasdeal-orders",
            });
            notice.onclick = () => { window.focus(); notice.close(); };
          }
        }
        known.current = ids;
      } catch { /* Admin session may have expired. Never display customer details. */ }
    };
    check();
    const timer = window.setInterval(check, 60000);
    return () => { active = false; window.clearInterval(timer); };
  }, []);
  const enable = async () => {
    if (typeof Notification === "undefined") return;
    const result = await Notification.requestPermission();
    setPermission(result);
    setEnabled(result === "granted");
  };
  return (
    <div className="mb-5 rounded border border-[#C9A45C]/25 p-3 text-xs text-[#F8F6F1]/70">
      <span>Alertes de nouvelles commandes : {enabled ? "actives pendant l'ouverture de cette page" : "désactivées"}. </span>
      {permission === "unsupported" ? <span>Notifications non prises en charge ici.</span> :
       permission === "denied" ? <span>Autorisation refusée dans le navigateur.</span> :
       <button type="button" onClick={enable} className="text-[#C9A45C] underline ml-1">Activer les alertes</button>}
      <p className="mt-1 text-[#F8F6F1]/45">La page doit rester ouverte. Les notifications iPhone en arrière-plan nécessitent une installation PWA et un service push distinct.</p>
    </div>
  );
}

export default function AdminDashboard() {
  useSeo({ title: "Administration" });
  const { user, logout } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("onglet") || "apercu";
  const Current = (TABS.find((t) => t[0] === tab) || TABS[0])[3];

  return (
    <div className="min-h-screen bg-[#0B0B0B] lg:flex" data-testid="admin-dashboard">
      <aside className="lg:w-64 lg:min-h-screen border-b lg:border-b-0 lg:border-r border-[#C9A45C]/15 bg-[#080808] lg:sticky lg:top-0 lg:h-screen flex lg:flex-col">
        <div className="hidden lg:flex items-center gap-3 p-6 border-b border-[#C9A45C]/15">
          <img src="/logo-nasdeal.jpg" alt="NasDeal" className="h-12 w-12" />
          <div><p className="font-display text-xl">NasDeal</p><p className="text-[10px] tracking-[0.2em] uppercase text-[#C9A45C]">Administration</p></div>
        </div>
        <nav className="flex lg:flex-col overflow-x-auto no-scrollbar lg:p-4 gap-1 flex-1">
          {TABS.map(([id, label, Icon]) => (
            <button key={id} onClick={() => setSp({ onglet: id })} data-testid={`admin-dashboard-tab-${id}`}
              className={`flex items-center gap-3 px-4 py-3 text-sm whitespace-nowrap transition-colors duration-300 ${tab === id ? "bg-[#C9A45C]/10 text-[#C9A45C] lg:border-l-2 border-b-2 lg:border-b-0 border-[#C9A45C]" : "text-[#F8F6F1]/65 hover:text-[#F8F6F1]"}`}>
              <Icon className="h-4 w-4" />{label}
            </button>
          ))}
        </nav>
        <div className="hidden lg:block p-4 border-t border-[#C9A45C]/15 space-y-2">
          <p className="text-xs text-[#F8F6F1]/45 truncate px-4">{user?.email}</p>
          <Link to="/" target="_blank" className="flex items-center gap-3 px-4 py-2 text-sm text-[#F8F6F1]/65 hover:text-[#C9A45C]"><ExternalLink className="h-4 w-4" />Voir la boutique</Link>
          <button onClick={logout} className="flex items-center gap-3 px-4 py-2 text-sm text-[#F8F6F1]/65 hover:text-red-400" data-testid="admin-logout-button"><LogOut className="h-4 w-4" />Déconnexion</button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 p-5 sm:p-8 lg:p-10">
        <div className="lg:hidden flex justify-end gap-4 mb-4 text-xs">
          <Link to="/" className="text-[#F8F6F1]/60">Boutique</Link>
          <button onClick={logout} className="text-[#F8F6F1]/60" data-testid="admin-logout-button-mobile">Déconnexion</button>
        </div>
        <AdminPushSetup />
        <OrderAlerts />
        <Current />
      </main>
    </div>
  );
}
